import React, { useRef, useState, useCallback, useEffect, useMemo, Suspense } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { AdaptiveDpr, ContactShadows, Preload } from "@react-three/drei";
import { createXRStore, XR } from "@react-three/xr";
import { useNavigate } from "react-router-dom";

import { COLORS, HOTSPOTS, POINTS_OF_INTEREST, DEFAULT_CAM_POS } from "./zone/constants";
import {
  TimeOfDayContext,
  TimeUpdater,
  useTimeOfDayState,
  CrowdContext,
  useCrowdState,
} from "./zone/context";
import { useUrlSceneState } from "./zone/urlState";
import DynamicLighting from "./zone/lights/DynamicLighting";
import Ground from "./zone/scene/Ground";
import Fountain from "./zone/scene/Fountain";
import Trees from "./zone/props/Trees";
import Benches from "./zone/props/Benches";
import LampPosts from "./zone/props/LampPosts";
import Buildings from "./zone/props/Buildings";
import Banners from "./zone/props/Banners";
import EntranceArch from "./zone/props/EntranceArch";
import Hotspot from "./zone/props/Hotspot";
import NPCs from "./zone/props/NPCs";
import SportsArena from "./zone/arena/SportsArena";
import { useArenaState } from "./zone/arena/arenaState";
import Controls from "./zone/controls/Controls";
import CameraStateBridge from "./zone/controls/CameraStateBridge";
import GroundTeleport from "./zone/controls/GroundTeleport";
import MobileJoystick from "./zone/controls/MobileJoystick";
import ArenaTouchControls from "./zone/controls/ArenaTouchControls";
import TourController from "./zone/controls/TourController";
import ZoneHUD from "./zone/hud/ZoneHUD";
import PlayNowOverlay from "./zone/hud/PlayNowOverlay";
import HowToPlay from "./zone/hud/HowToPlay";
import GameOverOverlay from "./zone/hud/GameOverOverlay";
import CampusMap from "./zone/hud/CampusMap";
import LiveMinimap from "./zone/hud/LiveMinimap";
import { useBellTolls } from "./zone/audio/useBellTolls";
import BellTollController from "./zone/audio/BellTollController";
import { useAvatar } from "./zone/avatar/useAvatar";
import { useAvatarUnlock } from "./zone/avatar/useAvatarUnlock";
import { useIsTouch } from "./zone/hooks/useDevice";

// Module-scoped XR store, per @react-three/xr v6 docs.
const xrStore = createXRStore({
  emulate: false,
  controller: { teleportPointer: true },
});

function SceneContent({
  onHotspot,
  activeHotspot,
  audioApi,
  onBellVisibilityChange,
  testTickRef,
  teleportRef,
  arenaApi,
}) {
  return (
    <>
      <DynamicLighting />
      <Ground />
      <Buildings />
      <Banners />
      <EntranceArch />
      <Trees />
      <Benches />
      <LampPosts />
      <Fountain />
      <NPCs />
      {arenaApi && <SportsArena arenaApi={arenaApi} />}
      {HOTSPOTS.map((h) => (
        <Hotspot key={h.id} data={h} isActive={activeHotspot === h.id} onClick={onHotspot} />
      ))}
      {POINTS_OF_INTEREST.map((p) => (
        <Hotspot
          key={p.id}
          data={p}
          isActive={activeHotspot === p.id}
          onClick={onHotspot}
          scale={0.7}
        />
      ))}
      <ContactShadows position={[0, 0.04, 0]} opacity={0.45} scale={120} blur={2.4} far={20} />
      <BellTollController
        audioApi={audioApi}
        onVisibilityChange={onBellVisibilityChange}
        testTickRef={testTickRef}
      />
      <GroundTeleport teleportRef={teleportRef} />
      <Preload all />
    </>
  );
}

export default function ZoneScene({
  onHotspotSelect,
  initialMode = "orbit",
  initialTimeMode = "auto",
  showOverlay = true,
  className = "",
  style = {},
}) {
  const navigate = useNavigate();
  const { camPos, targetPos, urlTimeMode } = useUrlSceneState();
  const isTouch = useIsTouch();

  const [mode, setMode] = useState(initialMode);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [showHotspotPanel, setShowHotspotPanel] = useState(null);
  const [bellVisible, setBellVisible] = useState(false);

  const orbitControlsRef = useRef();
  const cameraStateRef = useRef(null);
  const testTickRef = useRef(null);
  // Shared input source for ThirdPersonWalker (joystick + sprint flag).
  const joystickInputRef = useRef({ x: 0, y: 0, sprint: false });
  // Teleport target sink — XR controller / right-click sets this; walker consumes.
  const teleportRef = useRef(null);

  // Publish a global walker-teleport handle so the ArenaHUD's "Go to Arena"
  // button (deep inside SportsArena) can move the walker to the entrance
  // plaza without having to thread the ref down through the whole scene.
  // Also publish __zone_enter_walk so the Play Now landing overlay can flip
  // touch visitors straight into walk mode (so the on-screen joystick shows).
  useEffect(() => {
    window.__zone_walker_teleport = (x, y, z) => {
      teleportRef.current = new THREE.Vector3(x, y || 0, z);
    };
    window.__zone_enter_walk = () => setMode("walk");
    return () => {
      try {
        delete window.__zone_walker_teleport;
        delete window.__zone_enter_walk;
      } catch (e) { /* ignore */ }
    };
  }, []);

  const { timeMode, setTimeMode, autoLabel, contextValue } = useTimeOfDayState(
    initialTimeMode,
    urlTimeMode
  );
  const crowd = useCrowdState();

  const audioApi = useBellTolls();
  const avatarApi = useAvatar();
  const avatarUnlock = useAvatarUnlock();
  const arenaApi = useArenaState();

  // Unlock audio on first user gesture.
  useEffect(() => {
    const handler = () => audioApi.unlock();
    window.addEventListener("pointerdown", handler);
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
  }, [audioApi]);

  // Dev/test helper.
  useEffect(() => {
    window.__tabBellToll = (count = 1) => {
      audioApi.unlock();
      audioApi.toll(count);
    };
    return () => {
      try {
        delete window.__tabBellToll;
      } catch (e) {
        /* ignore */
      }
    };
  }, [audioApi]);

  const handleHotspot = useCallback(
    (h) => {
      setActiveHotspot(h.id);
      setShowHotspotPanel(h);
      if (h.id === "bell") {
        audioApi.unlock();
        audioApi.toll(1);
      }
      onHotspotSelect && onHotspotSelect(h);
    },
    [audioApi, onHotspotSelect]
  );

  const handleHotspotEnter = useCallback(
    (h) => {
      onHotspotSelect && onHotspotSelect(h);
      if (h && h.route) navigate(h.route);
    },
    [navigate, onHotspotSelect]
  );

  const handleHotspotClose = useCallback(() => {
    setShowHotspotPanel(null);
    setActiveHotspot(null);
  }, []);

  const enterWalk = useCallback(() => setMode("walk"), []);
  const enterOrbit = useCallback(() => setMode("orbit"), []);

  // ---------------------------------------------------------------------------
  // Guided tour — fly the orbit camera through every marker (4 landmarks +
  // 10 POIs). Lives as a ref so the per-frame controller doesn't trigger
  // React renders; the React state below is just for the HUD button label.
  // ---------------------------------------------------------------------------
  const TOUR_MARKERS = useMemo(
    () => [...HOTSPOTS, ...POINTS_OF_INTEREST],
    []
  );
  const tourRef = useRef({
    active: false,
    index: 0,
    phase: "moving",
    phaseStart: 0,
    fromPos: null,
    fromTarget: null,
    transitionTime: 1.6,
    lingerTime: 3.0,
  });
  const [tourActive, setTourActive] = useState(false);
  const [tourIndex, setTourIndex] = useState(0);

  const startTour = useCallback(() => {
    if (tourRef.current.active) return;
    if (mode !== "orbit") setMode("orbit");
    // Snapshot of current camera + target — TourController fills these on
    // the first frame from the live three context.
    tourRef.current.active = true;
    tourRef.current.index = 0;
    tourRef.current.phase = "moving";
    tourRef.current.phaseStart = 0; // overwritten on first useFrame tick
    tourRef.current.fromPos = null;
    tourRef.current.fromTarget = null;
    setTourActive(true);
    setTourIndex(0);
  }, [mode]);

  const stopTour = useCallback(() => {
    tourRef.current.active = false;
    setTourActive(false);
    setShowHotspotPanel(null);
    setActiveHotspot(null);
  }, []);

  // Initialise the tour ref on the FIRST frame after activation so we capture
  // the user's actual camera position/target (orbitControlsRef may not be set
  // at the moment startTour() fires).
  useEffect(() => {
    if (!tourActive) return;
    let raf = 0;
    const setup = () => {
      const ctrl = orbitControlsRef.current;
      if (!ctrl) {
        raf = requestAnimationFrame(setup);
        return;
      }
      // We don't have direct access to camera here — TourController grabs it
      // on the first frame instead. Just mark phaseStart=0 so it initialises
      // itself from the live three state.
      tourRef.current.phaseStart = -1;
    };
    setup();
    return () => cancelAnimationFrame(raf);
  }, [tourActive]);

  const handleTourArrive = useCallback(
    (marker) => {
      setTourIndex(tourRef.current.index);
      setActiveHotspot(marker.id);
      setShowHotspotPanel(marker);
    },
    []
  );
  const handleTourComplete = useCallback(() => {
    setTourActive(false);
    setShowHotspotPanel(null);
    setActiveHotspot(null);
  }, []);

  // If the user switches to walk mode mid-tour, gracefully stop the tour.
  useEffect(() => {
    if (mode !== "orbit" && tourActive) stopTour();
  }, [mode, tourActive, stopTour]);

  const getPose = useCallback(() => {
    if (cameraStateRef.current && cameraStateRef.current.getPose) {
      return cameraStateRef.current.getPose();
    }
    return null;
  }, []);

  return (
    <div
      data-testid="zone-scene-root"
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100dvh",
        minHeight: "100vh",
        background: COLORS.twilightTop,
        overflow: "hidden",
        ...style,
      }}
    >
      <Canvas
        data-testid="zone-scene-canvas"
        shadows
        dpr={[1, 2]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        camera={{
          position: camPos || DEFAULT_CAM_POS,
          fov: 55,
          near: 0.1,
          far: 500,
        }}
      >
        <XR store={xrStore}>
          <TimeOfDayContext.Provider value={contextValue}>
            <CrowdContext.Provider value={crowd.contextValue}>
              <Suspense fallback={null}>
                <TimeUpdater />
                <SceneContent
                  onHotspot={handleHotspot}
                  activeHotspot={activeHotspot}
                  audioApi={audioApi}
                  onBellVisibilityChange={setBellVisible}
                  testTickRef={testTickRef}
                  teleportRef={teleportRef}
                  arenaApi={arenaApi}
                />
                <Controls
                  mode={mode}
                  controlsRef={orbitControlsRef}
                  initialCam={camPos}
                  initialTarget={targetPos}
                  avatarUrl={avatarApi.avatarUrl}
                  isRenderableInScene={avatarApi.isRenderableInScene}
                  joystickInputRef={joystickInputRef}
                  teleportRef={teleportRef}
                />
                <TourController
                  tourRef={tourRef}
                  controlsRef={orbitControlsRef}
                  markers={TOUR_MARKERS}
                  onArrive={handleTourArrive}
                  onComplete={handleTourComplete}
                />
                <CameraStateBridge
                  stateRef={cameraStateRef}
                  controlsRef={orbitControlsRef}
                />
                <AdaptiveDpr pixelated />
              </Suspense>
            </CrowdContext.Provider>
          </TimeOfDayContext.Provider>
        </XR>
      </Canvas>

      {showOverlay && (
        <ZoneHUD
          mode={mode}
          enterOrbit={enterOrbit}
          enterWalk={enterWalk}
          timeMode={timeMode}
          autoLabel={autoLabel}
          setTimeMode={setTimeMode}
          bellVisible={bellVisible}
          hotspot={showHotspotPanel}
          onHotspotEnter={handleHotspotEnter}
          onHotspotClose={handleHotspotClose}
          activeHotspot={activeHotspot}
          getPose={getPose}
          muted={audioApi.muted}
          setMuted={audioApi.setMuted}
          volume={audioApi.volume}
          setVolume={audioApi.setVolume}
          avatarApi={avatarApi}
          avatarUnlock={avatarUnlock}
          xrStore={xrStore}
          isTouch={isTouch}
          crowdDensity={crowd.density}
          setCrowdDensity={crowd.setDensity}
          tourActive={tourActive}
          tourIndex={tourIndex}
          tourCount={TOUR_MARKERS.length}
          startTour={startTour}
          stopTour={stopTour}
          arenaApi={arenaApi}
        />
      )}

      {/* Mobile joystick — only on touch devices, only while walking */}
      <MobileJoystick
        inputRef={joystickInputRef}
        visible={isTouch && mode === "walk"}
      />

      {/* Arena touch controls — bottom-right joystick + FIRE button. Only on
          touch, only while the ArenaHUD is open so we don't crowd the quad
          view for visitors who aren't playing. */}
      <ArenaTouchControls arenaApi={arenaApi} visible={isTouch} />

      {/* Floating "How to Play" trigger — opens a controls overlay */}
      <HowToPlay isTouch={isTouch} />

      {/* Fast-travel Campus Map — SVG minimap with teleport hotspots */}
      {arenaApi && <CampusMap arenaApi={arenaApi} isTouch={isTouch} />}

      {/* Live 2D minimap — bottom-right, shows "You Are Here" dot tracking
          the visitor's camera position in real time. */}
      {arenaApi && <LiveMinimap arenaApi={arenaApi} isTouch={isTouch} />}

      {/* First-visit landing card — "Play Now" jumps straight to the arena */}
      {arenaApi && (
        <PlayNowOverlay arenaApi={arenaApi} isTouch={isTouch} />
      )}

      {/* Game Over — appears when a round is won (score threshold reached
          for basketball/soccer/football, or 10 pitches thrown for baseball).
          Owns Play Again + Next Sport, both driven by arenaApi. */}
      {arenaApi && (
        <GameOverOverlay arenaApi={arenaApi} derbyDone={!!arenaApi.derbyDone} />
      )}
    </div>
  );
}
